import type { BackupRecoveryKit, EncryptedRecoveryKit } from './types';
import { brandingFilenamePrefix, getBranding } from './branding';

const BASE64URL = /^[A-Za-z0-9_-]+$/;
const REPOSITORY_ID = /^[0-9a-f]{64}$/;
const GOOGLE_ID = /^[A-Za-z0-9_-]{1,255}$/;
const RESTIC_PASSWORD = /^[A-Za-z0-9_-]{64}$/;

function cryptoApi(): Crypto {
  if (!globalThis.crypto?.subtle) throw new Error('This browser does not provide the Web Crypto API required for recovery kits.');
  return globalThis.crypto;
}

function decodeBase64Url(value: string): ArrayBuffer {
  if (!BASE64URL.test(value)) throw new Error('Recovery data is not valid base64url.');
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(value.length / 4) * 4, '=');
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes.buffer;
}

function encodeBase64Url(value: ArrayBuffer | Uint8Array): string {
  const bytes = value instanceof Uint8Array ? value : new Uint8Array(value);
  let binary = '';
  for (let index = 0; index < bytes.length; index += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

export async function createRecoveryKeyPair(): Promise<{ privateKey: CryptoKey; publicKey: string }> {
  const pair = await cryptoApi().subtle.generateKey(
    { name: 'RSA-OAEP', modulusLength: 3072, publicExponent: new Uint8Array([1, 0, 1]), hash: 'SHA-256' },
    true,
    ['encrypt', 'decrypt'],
  );
  const publicKey = await cryptoApi().subtle.exportKey('spki', pair.publicKey);
  return { privateKey: pair.privateKey, publicKey: encodeBase64Url(publicKey) };
}

export async function encryptPasswordForController(password: string, controllerPublicKey: string): Promise<string> {
  if (!RESTIC_PASSWORD.test(password) || !BASE64URL.test(controllerPublicKey) || controllerPublicKey.length > 1024) {
    throw new Error('The recovery kit or controller public key is invalid.');
  }
  const key = await cryptoApi().subtle.importKey(
    'spki',
    decodeBase64Url(controllerPublicKey),
    { name: 'RSA-OAEP', hash: 'SHA-256' },
    false,
    ['encrypt'],
  );
  return encodeBase64Url(await cryptoApi().subtle.encrypt(
    { name: 'RSA-OAEP' },
    key,
    new TextEncoder().encode(password),
  ));
}

function isRecoveryKit(value: unknown): value is BackupRecoveryKit {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const kit = value as Record<string, unknown>;
  const instructions = kit.instructions;
  return kit.schemaVersion === 1 && kit.product === 'Streamline' && kit.provider === 'google_drive' &&
    typeof kit.createdAt === 'string' && Number.isFinite(Date.parse(kit.createdAt)) &&
    typeof kit.resticPassword === 'string' && RESTIC_PASSWORD.test(kit.resticPassword) &&
    typeof kit.googleAccountEmail === 'string' && kit.googleAccountEmail.length >= 3 && kit.googleAccountEmail.length <= 320 &&
    typeof kit.googleAccountId === 'string' && GOOGLE_ID.test(kit.googleAccountId) &&
    typeof kit.googleDriveFolderId === 'string' && GOOGLE_ID.test(kit.googleDriveFolderId) &&
    typeof kit.googleDriveFolderName === 'string' && kit.googleDriveFolderName.length >= 1 && kit.googleDriveFolderName.length <= 180 &&
    typeof kit.repositoryId === 'string' && REPOSITORY_ID.test(kit.repositoryId) &&
    typeof kit.repositoryLayout === 'string' && kit.repositoryLayout === 'pinned-drive-folder/repository' &&
    typeof kit.resticHost === 'string' && kit.resticHost.length >= 1 && kit.resticHost.length <= 255 &&
    typeof kit.controllerVersion === 'string' && kit.controllerVersion.length <= 128 &&
    typeof kit.resticVersion === 'string' && kit.resticVersion.length <= 128 &&
    typeof kit.rcloneVersion === 'string' && kit.rcloneVersion.length <= 128 &&
    Array.isArray(instructions) && instructions.length <= 10 &&
    instructions.every((instruction) => typeof instruction === 'string' && instruction.length <= 500);
}

export async function decryptRecoveryKit(envelope: EncryptedRecoveryKit, privateKey: CryptoKey): Promise<BackupRecoveryKit> {
  const encryptedKey = decodeBase64Url(envelope.encryptedKey);
  const aesKeyBytes = await cryptoApi().subtle.decrypt({ name: 'RSA-OAEP' }, privateKey, encryptedKey);
  const aesKey = await cryptoApi().subtle.importKey('raw', aesKeyBytes, { name: 'AES-GCM' }, false, ['decrypt']);
  const ciphertext = new Uint8Array(decodeBase64Url(envelope.ciphertext));
  const tag = new Uint8Array(decodeBase64Url(envelope.tag));
  const combined = new Uint8Array(ciphertext.length + tag.length);
  combined.set(ciphertext);
  combined.set(tag, ciphertext.length);
  const plaintext = await cryptoApi().subtle.decrypt(
    { name: 'AES-GCM', iv: decodeBase64Url(envelope.iv), tagLength: 128 },
    aesKey,
    combined,
  );
  let parsed: unknown;
  try {
    parsed = JSON.parse(new TextDecoder().decode(plaintext));
  } catch {
    throw new Error('The controller returned an invalid recovery kit.');
  }
  if (!isRecoveryKit(parsed)) throw new Error('The controller returned an invalid recovery kit.');
  return parsed;
}

export async function readRecoveryKitFile(file: File): Promise<BackupRecoveryKit> {
  if (file.size < 100 || file.size > 64 * 1024 || file.type && !['application/json', 'text/json', 'text/plain'].includes(file.type)) {
    throw new Error(`Choose a ${getBranding().productName} recovery-kit JSON file smaller than 64 KB.`);
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(await file.text());
  } catch {
    throw new Error('The selected file is not valid JSON.');
  }
  if (!isRecoveryKit(parsed)) throw new Error(`The selected file is not a valid ${getBranding().productName} recovery kit.`);
  return parsed;
}

export function downloadRecoveryKit(kit: BackupRecoveryKit): void {
  const timestamp = kit.createdAt.slice(0, 10);
  const blob = new Blob([`${JSON.stringify(kit, null, 2)}\n`], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${brandingFilenamePrefix()}-recovery-kit-${timestamp}-${kit.repositoryId.slice(0, 8)}.json`;
    anchor.rel = 'noopener';
    anchor.click();
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}
